import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { ArrowRight, MapPin, Phone, Mail, Tag } from 'lucide-react';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Landing = () => {
  const navigate = useNavigate();
  const [galleryCategories, setGalleryCategories] = useState([]);
  const [user, setUser] = useState(null);

  useEffect(() => {
    fetchGalleryCategories();
    checkUser();
  }, []);

  const checkUser = () => {
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }
  };

  const fetchGalleryCategories = async () => {
    try {
      const response = await axios.get(`${API}/gallery-categories`);
      setGalleryCategories(response.data);
    } catch (error) {
      console.error('Error fetching gallery categories:', error);
    }
  };

  const defaultGalleryCategories = [
    {
      id: 'lightboxes-preset',
      name: 'Lightboxes - Preset Sizes',
      description: 'Illuminated display solutions in standard sizes',
      image: 'https://customer-assets.emergentagent.com/job_duraplast-mfg/artifacts/imjxfs2o_Alphateta%20and%20Pioneer%20Lightbox.png',
      col_span: 'col-span-8'
    },
    {
      id: 'custom-signage',
      name: 'Custom Signage',
      description: 'Bespoke signage solutions for businesses',
      image: '',
      col_span: 'col-span-4'
    },
    {
      id: 'acrylic-fabrication',
      name: 'Acrylic Fabrication',
      description: 'Precision-cut acrylic products',
      image: '',
      col_span: 'col-span-4'
    },
    {
      id: 'point-of-sale',
      name: 'Point of Sale Displays',
      description: 'Retail display solutions',
      image: '',
      col_span: 'col-span-4'
    },
    {
      id: 'vehicle-graphics',
      name: 'Vehicle Graphics',
      description: 'Vehicle branding and wraps',
      image: '',
      col_span: 'col-span-4'
    },
    {
      id: 'dimensional-letters',
      name: 'Dimensional Letters',
      description: '3D lettering and signage',
      image: '',
      col_span: 'col-span-4'
    },
    {
      id: 'event-displays',
      name: 'Event Displays',
      description: 'Exhibition and event signage',
      image: '',
      col_span: 'col-span-4'
    },
    {
      id: 'retail-fixtures',
      name: 'Retail Fixtures',
      description: 'Custom retail installations',
      image: '',
      col_span: 'col-span-4'
    }
  ];

  const displayGalleryCategories = galleryCategories.length > 0 ? galleryCategories : defaultGalleryCategories;

  return (
    <div className="pt-16">
      {/* Hero Section with Sidebar */}
      <section className="relative min-h-[90vh] flex overflow-hidden">
        {/* Background Image - Full Width */}
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_duraplast-mfg/artifacts/vtbomwlm_Netball%20WC.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        >
          <div className="absolute inset-0 bg-[#050505]/60"></div>
        </div>

        {/* Left Sidebar - Gallery Categories (Transparent Overlay) */}
        <div className="hidden lg:flex w-72 flex-shrink-0 z-20 relative">
          <div className="w-full p-6 pt-28 overflow-y-auto max-h-screen bg-black/40 backdrop-blur-sm border-r border-white/10" style={{ direction: 'rtl' }}>
            <div style={{ direction: 'ltr' }}>
              <h3 className="text-lg font-bold text-[#D4AF37] mb-4">Gallery Categories</h3>
              <div className="flex flex-col gap-2">
                {displayGalleryCategories.map((category, index) => (
                  <button
                    key={category.id}
                    onClick={() => navigate(`/gallery/${category.id}`)}
                    className="text-left px-4 py-3 rounded-lg border border-white/20 hover:border-[#D4AF37]/50 hover:bg-[#D4AF37]/20 bg-black/30 transition-all duration-300 group"
                    data-testid={`sidebar-category-${index}`}
                  >
                    <span className="text-sm text-white/90 group-hover:text-[#D4AF37] transition-colors">
                      {category.name}
                    </span>
                  </button>
                ))}
              </div>
              <Button 
                onClick={() => navigate('/gallery')} 
                className="w-full mt-4 border-2 border-[#D4AF37] bg-black/30 hover:bg-[#D4AF37]/30 text-[#D4AF37] text-sm"
              >
                View All Gallery
              </Button>
            </div>
          </div>
        </div>

        {/* Main Hero Content */}
        <div className="flex-1 relative flex items-center justify-center z-10">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="relative text-center px-6 max-w-5xl mt-8"
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-black mb-4 tracking-tight" data-testid="hero-title">
              MANUFACTURING
              <br />
              <span className="text-[#FF4500]">EXCELLENCE</span>
            </h1>
            <p className="text-base md:text-lg text-white/80 mb-6 max-w-2xl mx-auto" data-testid="hero-subtitle">
              Premium signage, acrylic displays, and promotional products for South Africa's leading businesses
            </p>
            
            {!user && (
              <div className="mb-6 p-6 border-2 border-[#D4AF37] bg-gradient-to-r from-[#D4AF37]/20 to-[#D4AF37]/10 backdrop-blur-sm max-w-2xl mx-auto shadow-[0_0_30px_rgba(212,175,55,0.3)]" data-testid="landing-discount-banner">
                <div className="flex flex-col items-center justify-center gap-3">
                  <div className="flex items-center gap-4">
                    <Tag className="text-[#D4AF37]" size={28} />
                    <div>
                      <p className="text-xl font-bold text-[#D4AF37] mb-1">
                        Become a Diamond Member
                      </p>
                      <p className="text-base text-white/90">
                        Preferential Pricing and Priority Production
                      </p>
                    </div>
                  </div>
                  <Button 
                    onClick={() => navigate('/auth')} 
                    className="border-2 border-[#D4AF37] bg-black hover:bg-black hover:shadow-[0_0_40px_rgba(212,175,55,0.8),0_0_60px_rgba(212,175,55,0.4)] text-[#D4AF37] hover:text-white transition-all duration-300 px-6 py-3 text-sm"
                    data-testid="landing-register-button"
                  >
                    Register for Diamond Access
                  </Button>
                </div>
              </div>
            )}
            
            <div className="flex gap-4 justify-center flex-wrap">
              <Button 
                onClick={() => navigate('/shop')} 
                data-testid="hero-shop-button"
                className="bg-[#FF4500] hover:bg-[#FF4500]/90 text-white uppercase tracking-widest font-bold px-6 py-4 text-base transition-all neon-glow-hover"
              >
                Shop Now <ArrowRight className="ml-2" size={18} />
              </Button>
              <Button 
                onClick={() => document.getElementById('categories').scrollIntoView({ behavior: 'smooth' })}
                data-testid="hero-explore-button"
                className="bg-gradient-to-r from-[#1e40af] to-[#3b82f6] hover:from-[#1e3a8a] hover:to-[#2563eb] text-white uppercase tracking-widest font-bold px-6 py-4 text-base transition-all shadow-[0_4px_20px_rgba(59,130,246,0.4)] hover:shadow-[0_6px_30px_rgba(59,130,246,0.6)]"
              >
                View Manufacturing Capabilities
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Who We Are Section */}
      <section id="who-we-are" className="py-24 px-6 md:px-12 lg:px-24 bg-[#050505] border-y border-white/10 scroll-mt-20">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-12 text-center" data-testid="who-we-are-title">WHO WE ARE</h2>
            <div className="flex flex-col lg:flex-row gap-12 items-start">
              {/* Photo on the left */}
              <div className="lg:w-1/3 flex-shrink-0">
                <div className="sticky top-32">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_duraplast-mfg/artifacts/fhm16ulf_image.png" 
                    alt="Conrad du Toit - Founder of Duraplast"
                    className="w-full rounded-lg border-2 border-[#D4AF37]/30 shadow-[0_0_30px_rgba(212,175,55,0.2)]"
                  />
                  <p className="text-center mt-4 text-[#D4AF37] font-semibold">Conrad du Toit</p>
                  <p className="text-center text-white/60 text-sm">Founder & Owner</p>
                </div>
              </div>
              
              {/* Description on the right */}
              <div className="lg:w-2/3 space-y-6">
                <p className="text-lg text-white/90 leading-relaxed font-semibold">
                  Duraplast is defined by precision, craftsmanship, and decades of industry expertise.
                </p>
                <p className="text-base text-white/80 leading-relaxed">
                  Founded and owned by Conrad du Toit, the business is backed by 31 years of experience in the signage, display, and plastic engineering sectors. This includes 14 years with South Africa's largest signage and engineering distributor, followed by 17 years of independent manufacturing and business leadership—with Duraplast now representing the culmination of that experience.
                </p>
                <p className="text-base text-white/80 leading-relaxed">
                  Conrad's career began at ground level as a junior internal salesman, progressing steadily to management, where he led head office operations before relocating to Cape Town in 2000. There, he managed both the Stikland and Paarden Eiland branches, gaining an in-depth understanding of materials, specifications, large-scale production, and the operational demands of national brands.
                </p>
                <p className="text-base text-white/80 leading-relaxed">
                  In 2008, he established his own manufacturing business, shifting from material supply to the design and production of refined, purpose-built products. Guided by a meticulous and hands-on approach, Conrad personally developed advanced technical expertise in CAD design, CNC routing, CO₂ laser cutting, toolpath creation, graphic design, and 3D modelling and rendering. This rare combination of design insight and manufacturing control ensures each project is both visually considered and technically precise.
                </p>
                <p className="text-base text-white/80 leading-relaxed">
                  Today, Duraplast operates as a founder-led studio, with Conrad overseeing every aspect of the business—from concept development and design through to production, quality control, and final delivery. This direct involvement allows for exceptional consistency, discretion, and attention to detail on every project.
                </p>
                <p className="text-base text-white/80 leading-relaxed">
                  Duraplast has been trusted to work directly and indirectly with some of South Africa's most recognisable brands, including <span className="text-white font-medium">Woolworths</span>, <span className="text-white font-medium">Ackermans</span>, <span className="text-white font-medium">Shoprite</span>, <span className="text-white font-medium">Checkers</span>, <span className="text-white font-medium">Crown National</span>, <span className="text-white font-medium">Cellucity</span>, <span className="text-white font-medium">Cock n Bull</span>, <span className="text-white font-medium">Red Bull</span>, and <span className="text-white font-medium">Yoco</span>, delivering national roll-outs, bespoke point-of-sale displays, precision acrylic components, and custom signage.
                </p>
                <p className="text-lg text-white/90 leading-relaxed font-semibold mt-8 border-l-4 border-[#D4AF37] pl-6">
                  At Duraplast, we believe that quality lies in the details. Every product is designed and manufactured with intention—balancing form, function, and durability—to deliver elevated solutions for brands that value excellence.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Gallery Section */}
      <section id="categories" className="py-24 px-6 md:px-12 lg:px-24 scroll-mt-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4 text-center" data-testid="categories-title">GALLERY</h2>
          <p className="text-center text-white/60 mb-16 text-lg">Explore our work and craftsmanship</p>
          
          <div className="max-w-7xl mx-auto">
            {/* Gallery Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {displayGalleryCategories.map((category, index) => (
                <motion.div
                  key={category.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  onClick={() => navigate(`/gallery/${category.id}`)}
                  data-testid={`category-card-${index}`}
                  className="group overflow-hidden cursor-pointer border border-white/10 hover:border-[#D4AF37]/50 transition-all duration-500 bg-white/5"
                >
                  {/* Title Above Image */}
                  <div className="p-4 bg-[#0a0a0a]">
                    <h3 className="text-lg font-bold text-white/70 group-hover:text-[#D4AF37] transition-colors" data-testid={`category-name-${index}`}>
                      {category.name}
                    </h3>
                  </div>
                  <div className="w-full h-72 bg-black/20 flex items-center justify-center overflow-hidden">
                    {category.image ? (
                      <img 
                        src={category.image} 
                        alt={category.name}
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <div className="text-white/20 text-6xl font-bold">{index + 1}</div>
                    )}
                  </div>
                  <div className="p-4 bg-[#0a0a0a]">
                    <p className="text-white/60 text-sm whitespace-pre-line">{category.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 px-6 md:px-12 lg:px-24 bg-white/5 border-t border-white/10 scroll-mt-32">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-12 text-center" data-testid="contact-title">GET IN TOUCH</h2>
            
            <div className="grid md:grid-cols-2 gap-12 mb-12">
              <div className="space-y-6">
                <h3 className="text-2xl font-bold mb-6">Contact Information</h3>
                <div className="flex items-start gap-4" data-testid="contact-address">
                  <MapPin className="text-[#D4AF37] mt-1" size={24} />
                  <div>
                    <p className="font-bold">Address</p>
                    <p className="text-white/70">10b Freedom Way, Montague Gardens, Cape Town, South Africa, 7441</p>
                  </div>
                </div>
                <div className="flex items-start gap-4" data-testid="contact-phone">
                  <Phone className="text-[#D4AF37] mt-1" size={24} />
                  <div>
                    <p className="font-bold">Phone</p>
                    <p className="text-white/70">+27 82 577 7773</p>
                  </div>
                </div>
                <div className="flex items-start gap-4" data-testid="contact-email">
                  <Mail className="text-[#D4AF37] mt-1" size={24} />
                  <div>
                    <p className="font-bold">Email</p>
                    <a href="mailto:info@duraplast.co.za" className="text-white/70 hover:text-[#D4AF37] transition-colors">info@duraplast.co.za</a>
                  </div>
                </div>
              </div>
              
              <div className="border border-white/10 overflow-hidden neon-glow" data-testid="google-map">
                <iframe 
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3309.2877882843716!2d18.511846!3d-33.8905144!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1dcc5d1e3f9a7e6f%3A0x4f8c8e8e8e8e8e8e!2s10b%20Freedom%20Way%2C%20Montague%20Gardens%2C%20Cape%20Town%2C%207441!5e0!3m2!1sen!2sza!4v1234567890"
                  width="100%"
                  height="300"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  title="Google Maps Location"
                ></iframe>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-white/10 text-center text-white/60">
        <p>&copy; 2025 Duraplast. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default Landing;